import { Image } from 'expo-image'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import appQRCode from '@/assets/success/app_qr_code.png'
import safepointsDe from '@/assets/success/safepoints_de.png'
import safepointsEn from '@/assets/success/safepoints_en.png'
import storeApple from '@/assets/success/store-apple.svg'
import storeGoogle from '@/assets/success/store-google.svg'
import { useFormatting } from '@/context/FormattingContext'
import { AirpodsClaimSteps } from '@/shared/components/flows/AirpodsCampaign'
import { resolveImageSrc } from '@/utils/imageSrc'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { COLOR } from '@ui-kit/constants'
import { caption } from '@ui-kit/core/typography'

const referralRewardsSchema = z.object({ advocate: z.number(), friend: z.number() })

type ReferralRewards = z.infer<typeof referralRewardsSchema>

const readStoredReferralRewards = (): ReferralRewards | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  try {
    const parsed = referralRewardsSchema.safeParse(
      JSON.parse(localStorage.getItem('gs_referralRewards') ?? 'null')
    )
    return parsed.success ? parsed.data : undefined
  } catch {
    return undefined
  }
}

const checkItemStyles = tv({
  slots: {
    container: 'flex-row items-start gap-2',
    icon: 'h-[18px] w-[18px] shrink-0 items-center justify-center',
    text: caption({ color: 'black', bold: true, className: 'flex-1 leading-[18px]' }),
  },
})

const insuranceStyles = tv({
  slots: {
    panel:
      'h-full w-full flex-col items-center gap-8 rounded-3xl bg-black px-7 py-10 lg:gap-12 lg:rounded-[40px] lg:px-6 lg:py-12',
    content: 'w-full max-w-[480px] gap-5 lg:gap-6',
    heading:
      'font-adieu-bold text-[22px] uppercase leading-[29px] text-white lg:text-[40px] lg:leading-[48px]',
    card: 'w-full gap-4 rounded-[20px] border border-grey-200 bg-white p-6 lg:bg-grey-200',
    cardTitle:
      'font-inter-semibold text-[15px] leading-[22px] text-black lg:font-inter-medium lg:text-[20px] lg:leading-[30px]',
    summaryRows: 'gap-4',
    summaryRow: 'flex-row items-center justify-between gap-4',
    summaryLabel:
      'font-inter-regular text-sm leading-[21px] text-[#8C8C8C] lg:font-inter-medium lg:text-base lg:leading-6 lg:text-grey-700',
    summaryPrice:
      'font-inter-semibold text-[15px] leading-[18px] text-black lg:text-[24px] lg:leading-[29px]',
    referralRow: 'flex-row items-stretch gap-2',
    referralLinkBox:
      'min-w-0 flex-1 overflow-hidden rounded-lg border border-[#DDDDDD] bg-white px-2.5 py-3.5',
    referralLinkText: 'font-inter-medium text-base leading-6 text-grey-700',
    copyButton: 'shrink-0 items-center justify-center rounded-lg bg-black px-4 py-3.5',
    copyButtonText: 'font-inter-medium text-sm leading-[21px] text-white',
    disclaimer:
      'font-inter-regular text-sm leading-5 text-white/80 lg:text-xs lg:leading-[18px] lg:text-white',
  },
})

const appStyles = tv({
  slots: {
    panel: 'w-full gap-6 rounded-[40px] bg-grey-200 px-6 py-10 lg:px-12',
    media: 'hidden',
    mediaQrTile:
      'items-center justify-center rounded-2xl bg-white p-2.5 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]',
    mediaQrImage: 'h-[120px] w-[120px]',
    body: 'gap-6',
    section: 'gap-6',
    sectionTitle: 'font-inter-semibold text-[24px] leading-[29px] text-black',
    contentRow: 'flex-row flex-wrap items-center gap-6',
    checkList: 'shrink grow basis-[200px] gap-3',
    safepointsImage: 'h-[180px] w-[180px] shrink-0',
    qrTile:
      'h-[160px] w-[160px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-[0px_4px_17px_0px_rgba(0,0,0,0.08)]',
    qrImage: 'h-[136px] w-[136px]',
    storeRow: 'flex-row flex-wrap gap-4',
    storeBadge: 'h-[44px] w-[151px]',
  },
  variants: {
    airpods: {
      true: {
        panel:
          'gap-0 overflow-hidden rounded-3xl bg-grey-100 px-0 py-0 lg:gap-6 lg:rounded-[40px] lg:bg-grey-200 lg:px-12 lg:py-10',
        media: 'flex h-[170px] items-center justify-center bg-grey-200 lg:hidden',
        body: 'gap-4 px-5 pb-6 pt-5 lg:gap-6 lg:p-0',
        section: 'gap-4 lg:gap-6',
        sectionTitle: 'text-[20px] leading-6 lg:text-[24px] lg:leading-[29px]',
        qrTile: 'hidden lg:flex',
        storeRow: 'flex-nowrap gap-2 lg:flex-wrap lg:gap-4',
        storeBadge: 'h-[41px] w-[140px] lg:h-[44px] lg:w-[151px]',
      },
    },
  },
})

const successStyles = tv({
  slots: {
    grid: 'grid w-full max-w-[1300px] grid-cols-1 gap-6 lg:grid-cols-2',
    column: 'flex min-w-0',
    campaignColumn: 'flex min-w-0 flex-col gap-4',
  },
})

const CheckItem = ({ text }: { text: string }) => {
  const styles = checkItemStyles()
  return (
    <View className={styles.container()}>
      <View className={styles.icon()}>
        <PhosphorIcon name="CheckCircle" size={22} weight="fill" color={COLOR.black} />
      </View>
      <Text className={styles.text()}>{text}</Text>
    </View>
  )
}

const InsuranceAndReferralDetails = ({
  insuranceName,
  email,
  formattedStartDate,
  planTitle,
  formattedPremium,
  premiumLabel,
  referralLink,
  referralRewards,
}: {
  insuranceName: string
  email: string
  formattedStartDate: string
  planTitle: string
  formattedPremium: string
  premiumLabel: string
  referralLink: string
  referralRewards: ReferralRewards | undefined
}) => {
  const { t } = useTranslation()
  const formatter = useFormatting()
  const [copied, setCopied] = useState(false)
  const styles = insuranceStyles()

  useEffect(() => {
    if (!copied) {
      return
    }
    const id = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(id)
  }, [copied])

  const formatReward = (amount: number) =>
    formatter.formatCurrency(amount, { maximumFractionDigits: 0 })

  const referralTitle = referralRewards
    ? t('sharedFlows_common:steps.success.revamp.insurance.refer.rewardsTitle', {
        advocateReward: formatReward(referralRewards.advocate),
        friendReward: formatReward(referralRewards.friend),
      })
    : t('sharedFlows_common:steps.success.revamp.insurance.refer.fallbackTitle')

  const onCopy = () => {
    if (referralLink) {
      void navigator.clipboard.writeText(referralLink)
      setCopied(true)
    }
  }

  return (
    <View className={styles.panel()}>
      <View className={styles.content()}>
        <Text className={styles.heading()}>
          {t('sharedFlows_common:steps.success.purchase.title')}
        </Text>
      </View>

      <View className={styles.content()}>
        <View className={styles.card()}>
          <Text className={styles.cardTitle()}>
            {t('sharedFlows_common:steps.success.revamp.insurance.summary.title', {
              insuranceName,
            })}
          </Text>
          <View className={styles.summaryRows()}>
            {planTitle && (
              <View className={styles.summaryRow()}>
                <Text className={styles.summaryLabel()}>
                  {t('sharedFlows_common:orderSummary.plan')}
                </Text>
                <Text className={styles.summaryLabel()}>{planTitle}</Text>
              </View>
            )}
            {formattedStartDate && (
              <View className={styles.summaryRow()}>
                <Text className={styles.summaryLabel()}>
                  {t('sharedFlows_common:orderSummary.checkout.starting')}
                </Text>
                <Text className={styles.summaryLabel()}>{formattedStartDate}</Text>
              </View>
            )}
            <View className={styles.summaryRow()}>
              <Text className={styles.summaryLabel()}>{premiumLabel}</Text>
              <Text className={styles.summaryPrice()}>{formattedPremium}</Text>
            </View>
          </View>
        </View>

        {referralLink && (
          <View className={styles.card()}>
            <Text className={styles.cardTitle()}>{referralTitle}</Text>
            <View className={styles.referralRow()}>
              <View className={styles.referralLinkBox()}>
                <Text className={styles.referralLinkText()} numberOfLines={1}>
                  {referralLink}
                </Text>
              </View>
              <Pressable onPress={onCopy} className={styles.copyButton()}>
                <Text className={styles.copyButtonText()}>
                  {copied ? '✓' : t('sharedFlows_common:steps.success.revamp.insurance.refer.copy')}
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        <Text className={styles.disclaimer()}>
          {t('sharedFlows_common:steps.success.revamp.insurance.disclaimer', {
            userEmailAddress: email,
          })}
        </Text>
      </View>
    </View>
  )
}

const AppDetails = ({ language, showRewards }: { language: string; showRewards: boolean }) => {
  const { t } = useTranslation()
  const safepointsImg = language === 'de' ? safepointsDe : safepointsEn
  const styles = appStyles({ airpods: !showRewards })

  return (
    <View className={styles.panel()}>
      <View className={styles.media()}>
        <View className={styles.mediaQrTile()}>
          <Image
            source={resolveImageSrc(appQRCode)}
            alt="QR Code"
            className={styles.mediaQrImage()}
            contentFit="contain"
          />
        </View>
      </View>
      <View className={styles.body()}>
        {showRewards && (
          <View className={styles.section()}>
            <Text className={styles.sectionTitle()}>
              {t('sharedFlows_common:steps.success.revamp.rewardsProgram.title')}
            </Text>
            <View className={styles.contentRow()}>
              <Image
                source={resolveImageSrc(safepointsImg)}
                alt="Safepoints"
                className={styles.safepointsImage()}
                contentFit="contain"
              />
              <View className={styles.checkList()}>
                <CheckItem
                  text={t('sharedFlows_common:steps.success.revamp.rewardsProgram.point1')}
                />
                <CheckItem
                  text={t('sharedFlows_common:steps.success.revamp.rewardsProgram.point2')}
                />
                <CheckItem
                  text={t('sharedFlows_common:steps.success.revamp.rewardsProgram.point3')}
                />
              </View>
            </View>
          </View>
        )}

        <View className={styles.section()}>
          <Text className={styles.sectionTitle()}>
            {t('sharedFlows_common:steps.success.revamp.app.desktop.title')}
          </Text>
          <View className={styles.contentRow()}>
            <View className={styles.checkList()}>
              <CheckItem text={t('sharedFlows_common:steps.success.revamp.app.point1')} />
              <CheckItem text={t('sharedFlows_common:steps.success.revamp.app.point2')} />
              <CheckItem text={t('sharedFlows_common:steps.success.revamp.app.point3')} />
            </View>
            <View className={styles.qrTile()}>
              <Image
                source={resolveImageSrc(appQRCode)}
                alt="QR Code"
                className={styles.qrImage()}
                contentFit="contain"
              />
            </View>
          </View>
        </View>

        <View className={styles.storeRow()}>
          <Link
            href="https://itunes.apple.com/app/getsafe/id1296674334"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              source={storeApple?.src ?? storeApple}
              alt="App Store"
              className={styles.storeBadge()}
              contentFit="contain"
            />
          </Link>
          <Link
            href="https://play.google.com/store/apps/details?id=de.getsafe.nxt"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              source={storeGoogle?.src ?? storeGoogle}
              alt="Google Play"
              className={styles.storeBadge()}
              contentFit="contain"
            />
          </Link>
        </View>
      </View>
    </View>
  )
}

export type SuccessStepProps = {
  insuranceName: string
  language: string
  referralLink?: string
  planTitle?: string
  formattedPremium?: string
  premiumLabel?: string
  formattedStartDate?: string
  isAirpodsCampaign?: boolean
}

export const SuccessStep = (props: SuccessStepProps) => {
  const { watch } = useFormContext()
  const flowValues = watch()
  const email = (flowValues.email as string) ?? ''
  const referralLink =
    props.referralLink ||
    (typeof window !== 'undefined' ? localStorage.getItem('gs_referralUrl') ?? '' : '')
  const [referralRewards] = useState(readStoredReferralRewards)
  const styles = successStyles()

  return (
    <div className={styles.grid()}>
      <View className={styles.column()}>
        <InsuranceAndReferralDetails
          insuranceName={props.insuranceName}
          email={email}
          formattedStartDate={props.formattedStartDate ?? ''}
          planTitle={props.planTitle ?? ''}
          formattedPremium={props.formattedPremium ?? ''}
          premiumLabel={props.premiumLabel ?? ''}
          referralLink={referralLink}
          referralRewards={referralRewards}
        />
      </View>
      <View className={props.isAirpodsCampaign ? styles.campaignColumn() : styles.column()}>
        {props.isAirpodsCampaign && <AirpodsClaimSteps />}
        <AppDetails language={props.language} showRewards={!props.isAirpodsCampaign} />
      </View>
    </div>
  )
}
